import {useMutation} from '@apollo/react-hooks';

import type {AiFeatureCategory, AiFeatureType} from 'nutshell-graphql-types';
import type {
    UpdateSetting as UpdateSettingMutation,
    UpdateSettingVariables as UpdateSettingMutationVariables,
    GetAiFeatureUsage as GetAiFeatureUsageQuery,
    GetAiFeatureUsageVariables as GetAiFeatureUsageQueryVariables,
    GetAiFeatureEnabled as GetAiFeatureEnabledQuery,
    GetAiFeatureEnabledVariables as GetAiFeatureEnabledQueryVariables,
} from 'nutshell-graphql-types';

import UPDATE_SETTING from '../../../../core/graphql/mutations/update-setting.graphql';

import GET_AI_FEATURE_ENABLED from '../queries/get-ai-feature-enabled.graphql';
import GET_AI_FEATURE_USAGE from '../queries/get-ai-feature-usage.graphql';

export function useToggleAiFeatureSetting(): {
    toggleFeatureSetting: (
        settingName: string,
        newEnabled: boolean,
        types: AiFeatureType[],
        category: AiFeatureCategory
    ) => Promise<any>;
    isLoading: boolean;
} {
    const [updateSetting, {loading}] = useMutation<
        UpdateSettingMutation,
        UpdateSettingMutationVariables
    >(UPDATE_SETTING);

    return {
        toggleFeatureSetting: (
            settingName: string,
            newEnabled: boolean,
            types: AiFeatureType[],
            category: AiFeatureCategory
        ) => {
            const value = newEnabled ? '0' : '1';

            return updateSetting({
                variables: {
                    setting: {
                        name: settingName,
                        value,
                        isGlobal: true,
                    },
                },
                optimisticResponse: {
                    updateSetting: {
                        __typename: 'UpdateSettingPayload',
                        setting: {
                            __typename: 'Setting',
                            id: settingName,
                            value,
                        },
                    },
                },
                update: (proxy) => {
                    const cached = proxy.readQuery<
                        GetAiFeatureUsageQuery,
                        GetAiFeatureUsageQueryVariables
                    >({
                        query: GET_AI_FEATURE_USAGE,
                        variables: {category},
                    });

                    if (cached?.aiFeatureUsage) {
                        proxy.writeQuery({
                            query: GET_AI_FEATURE_USAGE,
                            variables: {category},
                            data: {
                                ...cached,
                                aiFeatureUsage: {
                                    ...cached.aiFeatureUsage,
                                    features: cached.aiFeatureUsage.features.map((feature) =>
                                        types.includes(feature.type)
                                            ? {...feature, isEnabled: newEnabled}
                                            : feature
                                    ),
                                },
                            },
                        });
                    }

                    // Not all types will have a cached GetAiFeatureEnabled entry —
                    // only those whose useIsAiFeatureEnabled hook has mounted.
                    // Apollo 2.x readQuery throws on cache misses, so we catch per-type.
                    for (const type of types) {
                        try {
                            const enabledCached = proxy.readQuery<
                                GetAiFeatureEnabledQuery,
                                GetAiFeatureEnabledQueryVariables
                            >({
                                query: GET_AI_FEATURE_ENABLED,
                                variables: {type},
                            });

                            if (enabledCached?.aiFeatureUsage) {
                                proxy.writeQuery({
                                    query: GET_AI_FEATURE_ENABLED,
                                    variables: {type},
                                    data: {
                                        ...enabledCached,
                                        aiFeatureUsage: {
                                            ...enabledCached.aiFeatureUsage,
                                            features: enabledCached.aiFeatureUsage.features.map(
                                                (feature) =>
                                                    feature.type === type
                                                        ? {
                                                              ...feature,
                                                              isEnabled: newEnabled,
                                                          }
                                                        : feature
                                            ),
                                        },
                                    },
                                });
                            }
                        } catch {
                            // Cache entry doesn't exist yet for this type — skip
                        }
                    }
                },
            });
        },
        isLoading: loading,
    };
}
